<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import OverflowMenu from '@/components/shared/OverflowMenu.vue'
import GalleryPicture from '@/components/organizer/galleries/GalleryPicture.vue'
import { GalleryStatus, GalleryType } from '@/types/galleries'
import type { OrganizerGallery } from '@/types/galleries'
import { formatBytes } from '@/utils/formatBytes'

const props = defineProps<{
  gallery: OrganizerGallery
  /** Route of the gallery detail page. */
  to: string
  position: number
  total: number
  dragging?: boolean
  dragOver?: boolean
  lifted?: boolean
}>()

const emit = defineEmits<{
  edit: []
  'toggle-status': []
  'move-to-start': []
  'move-to-end': []
  delete: []
  'drag-start': [event: DragEvent]
  'drag-over': [event: DragEvent]
  drop: [event: DragEvent]
  'drag-end': []
  'handle-keydown': [event: KeyboardEvent]
}>()

const { t } = useI18n()

const isPublished = computed(() => props.gallery.status === GalleryStatus.Published)

const isExternal = computed(() => props.gallery.type === GalleryType.External)

/** Host of the external link, shown instead of item counts. Falls back to the raw value for an unparsable URL. */
const externalHost = computed(() => {
  const url = props.gallery.external_url
  if (url === null) return ''
  try {
    return new URL(url).host
  } catch {
    return url
  }
})

const summaryLine = computed(() => {
  const parts: string[] = []
  const photos = props.gallery.counts.images
  const videos = props.gallery.counts.videos + props.gallery.counts.external_videos
  if (photos > 0) parts.push(t('galleries.card.photos', photos))
  if (videos > 0) parts.push(t('galleries.card.videos', videos))
  if (parts.length === 0) parts.push(t('galleries.card.empty'))
  return parts.join(' - ')
})
</script>

<template>
  <li
    class="group relative rounded-2xl bg-slate-800 border transition-colors"
    :class="[
      dragOver ? 'border-emerald-500' : 'border-slate-700 hover:border-slate-600',
      dragging ? 'opacity-50' : '',
      lifted ? 'ring-2 ring-emerald-500' : '',
    ]"
    :data-testid="`gallery-card-${gallery.id}`"
    @dragover="emit('drag-over', $event)"
    @drop="emit('drop', $event)"
  >
    <RouterLink
      :to="to"
      class="block rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
    >
      <div class="aspect-[4/3] rounded-t-2xl overflow-hidden bg-slate-900 flex items-center justify-center">
        <GalleryPicture
          v-if="gallery.cover !== null"
          :image="gallery.cover"
          variant="medium"
          sizes="(min-width: 1024px) 320px, 50vw"
        />
        <div v-else class="flex flex-col items-center gap-2 text-slate-600">
          <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21zm10.5-11.25h.008v.008h-.008V9.75z" />
          </svg>
          <span class="text-xs">{{ t('galleries.card.no_cover') }}</span>
        </div>
      </div>

      <div class="p-4 pr-14">
        <div class="flex items-center gap-2 mb-1.5">
          <span
            class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
            :class="isPublished ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-700 text-slate-300'"
          >
            <span class="w-1.5 h-1.5 rounded-full" :class="isPublished ? 'bg-emerald-400' : 'bg-slate-400'" aria-hidden="true" />
            {{ isPublished ? t('galleries.card.published') : t('galleries.card.draft') }}
          </span>
          <span
            v-if="isExternal"
            class="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-medium text-sky-300"
            data-testid="gallery-card-external-badge"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
            {{ t('galleries.card.external') }}
          </span>
        </div>
        <h3 class="text-base font-semibold text-white truncate">{{ gallery.title }}</h3>
        <p v-if="gallery.subtitle" class="text-sm text-slate-400 truncate">{{ gallery.subtitle }}</p>
        <p v-if="isExternal" class="mt-2 text-xs text-slate-500 truncate" data-testid="gallery-card-host">{{ externalHost }}</p>
        <p v-else class="mt-2 text-xs text-slate-500">{{ summaryLine }} - {{ formatBytes(gallery.bytes_used) }}</p>
      </div>
    </RouterLink>

    <!-- Drag handle: a separate button so the card link stays a plain link -->
    <button
      type="button"
      draggable="true"
      class="absolute top-2 left-2 w-8 h-8 flex items-center justify-center rounded-lg bg-black/50 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 cursor-grab transition-opacity"
      :class="lifted ? 'opacity-100' : ''"
      :aria-label="t('galleries.card.drag_handle', { title: gallery.title, position, total })"
      :data-reorder-handle="gallery.id"
      @dragstart="emit('drag-start', $event)"
      @dragend="emit('drag-end')"
      @keydown="emit('handle-keydown', $event)"
    >
      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M8 6a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm8 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zM8 13.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm8 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zM8 21a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm8 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
      </svg>
    </button>

    <div class="absolute bottom-3 right-3">
      <OverflowMenu :aria-label="t('galleries.card.actions', { title: gallery.title })" menu-class="w-48" teleport>
        <button type="button" class="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-700/50" @click="emit('edit')">
          {{ t('galleries.card.edit') }}
        </button>
        <button type="button" class="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-700/50" @click="emit('toggle-status')">
          {{ isPublished ? t('galleries.card.unpublish') : t('galleries.card.publish') }}
        </button>
        <button
          type="button"
          class="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-700/50 disabled:opacity-40"
          :disabled="position === 1"
          @click="emit('move-to-start')"
        >
          {{ t('galleries.card.move_start') }}
        </button>
        <button
          type="button"
          class="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-700/50 disabled:opacity-40"
          :disabled="position === total"
          @click="emit('move-to-end')"
        >
          {{ t('galleries.card.move_end') }}
        </button>
        <button type="button" class="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-slate-700/50" @click="emit('delete')">
          {{ t('galleries.card.delete') }}
        </button>
      </OverflowMenu>
    </div>
  </li>
</template>
